'use client';

import { useLocale, useTranslations } from 'next-intl';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { AddressFinder } from '@/components/Account/AddressFinder';
import { Button } from '@/components/ui/Button';
import { LinkButton } from '@/components/ui/Button/LinkButton';
import { Icon } from '@/components/ui/Icon';
import { Input } from '@/components/ui/Input';
import { Select, type SelectOption } from '@/components/ui/Select';
import { useCountryAreaFieldConfig } from '@/hooks/useCountryAreaFieldConfig';
import type { AddressSuggestion } from '@/hooks/useAddressSearch';
import { useAddressValidation } from '@/hooks/useAddressValidation';
import { formatUKPostcode, normalizeCountryCode, UK_POSTCODE_REGEX } from '@/lib/address';
import { getTitleFieldLabel, getTitleOptions, resolveTitleForForm } from '@/lib/forms/titleOptions';
import { getCountryFromLocale, getLanguageFromLocale } from '@/lib/i18n';
import {
  ABN_COUNTRIES,
  createCountryAwareSchema,
  VAT_COUNTRIES,
  type AddressFormSchemaMessages,
  type ExtendedAddressFormData,
} from '@/lib/validations/address';

import styles from './AddressForm.module.scss';

function isExtendedAddressFormField(
  field: PropertyKey | undefined
): field is keyof ExtendedAddressFormData {
  return typeof field === 'string';
}

// -------------------------------------------------------------------
// Types
// -------------------------------------------------------------------

interface AddressFormProps {
  /** Initial form values (for editing) */
  defaultValues?: Partial<ExtendedAddressFormData>;
  /** Available countries */
  countries: SelectOption[];
  /** Form submission handler */
  onSubmit: (
    data: ExtendedAddressFormData,
    options?: { isDefaultShipping?: boolean; isDefaultBilling?: boolean }
  ) => Promise<void>;
  /** Whether form is submitting */
  isSubmitting?: boolean;
  /** Submit button label */
  submitLabel?: string;
  /** Whether to show default address checkboxes (for create mode) */
  showDefaultCheckboxes?: boolean;
  /** Current default shipping address (to show as checked) */
  isDefaultShipping?: boolean;
  /** Current default billing address (to show as checked) */
  isDefaultBilling?: boolean;
  /** Whether to show address finder (autocomplete) */
  showAddressFinder?: boolean;
  /** Whether to validate with Melissa before submit */
  validateWithMelissa?: boolean;
  /** If provided, renders a "Cancel" button linking to this href */
  cancelHref?: string;
}

export interface AddressFormRef {
  /** Validate address using Melissa API */
  validateAddressUsingMelissa: () => Promise<boolean>;
}

// Japan skips Melissa validation
const SKIP_MELISSA_COUNTRIES = ['JP'];
const FALLBACK_COUNTRY = 'US';

// -------------------------------------------------------------------
// AddressForm
// Reusable address form for create/edit with Melissa integration
// -------------------------------------------------------------------

export const AddressForm = forwardRef<AddressFormRef, AddressFormProps>(
  function AddressFormComponent(
    {
      defaultValues,
      countries,
      onSubmit,
      isSubmitting = false,
      submitLabel = 'Save Address',
      showDefaultCheckboxes = false,
      isDefaultShipping: initialDefaultShipping = false,
      isDefaultBilling: initialDefaultBilling = false,
      showAddressFinder = true,
      validateWithMelissa = true,
      cancelHref,
    }: AddressFormProps,
    ref
  ) {
    const t = useTranslations('myAccount.addresses');
    const tForms = useTranslations('forms');
    const locale = useLocale();
    const marketCountry = normalizeCountryCode(getCountryFromLocale(locale));
    const languageCode = getLanguageFromLocale(locale);
    const validMarketCountry = countries.some((c) => c.value === marketCountry)
      ? marketCountry
      : undefined;
    // Edit mode: keep the stored address country. Create mode: channel, then US.
    const storedCountry = normalizeCountryCode(defaultValues?.country);
    const initialCountry = storedCountry || validMarketCountry || FALLBACK_COUNTRY;
    const [isDefaultShipping, setIsDefaultShipping] = useState(initialDefaultShipping);
    const [isDefaultBilling, setIsDefaultBilling] = useState(initialDefaultBilling);
    const [melissaErrors, setMelissaErrors] = useState<{ field: string; message: string }[]>([]);

    const { validate: validateAddress, isValidating } = useAddressValidation();

    const schemaMessages = useMemo(
      (): AddressFormSchemaMessages => ({
        requiredField: tForms('requiredField'),
        maxChars: (max) => tForms('maxChars', { max }),
        invalidCountry: tForms('invalidCountry'),
        phoneTooLong: t('phoneTooLong'),
        titleRequired: tForms('requiredField'),
        countryAreaRequired: t('countryAreaRequired'),
        abnRequired: t('abnRequiredForBusiness'),
      }),
      [t, tForms]
    );

    const {
      register,
      control,
      watch,
      setValue,
      setError,
      clearErrors,
      getValues,
      formState: { errors },
    } = useForm<ExtendedAddressFormData>({
      defaultValues: {
        firstName: '',
        lastName: '',
        firstNameKanji: '',
        lastNameKanji: '',
        companyName: '',
        streetAddress1: '',
        streetAddress2: '',
        city: '',
        postalCode: '',
        countryArea: '',
        vatNumber: '',
        abn: '',
        ...defaultValues,
        title: resolveTitleForForm(defaultValues?.title, initialCountry) ?? defaultValues?.title,
        country: initialCountry,
      },
    });

    const selectedCountry = watch('country') || initialCountry;
    const melissaCountryCode = selectedCountry;
    const companyName = watch('companyName');
    const titleCountry = selectedCountry || marketCountry;
    const titleOptions = useMemo(
      () => getTitleOptions(titleCountry, tForms),
      [titleCountry, tForms]
    );
    const titleFieldLabel = getTitleFieldLabel(titleCountry, tForms);
    const sortedCountries = useMemo(
      () =>
        [...countries].sort((left, right) =>
          left.label.localeCompare(right.label, languageCode, { sensitivity: 'base' })
        ),
      [countries, languageCode]
    );
    const countryAreaConfig = useCountryAreaFieldConfig(selectedCountry);

    const countryAwareSchema = useMemo(
      () =>
        createCountryAwareSchema({
          countryCode: selectedCountry,
          countryAreaRequired: countryAreaConfig.isRequired,
          messages: schemaMessages,
        }),
      [selectedCountry, countryAreaConfig.isRequired, schemaMessages]
    );

    const showVatField = VAT_COUNTRIES.includes(selectedCountry);
    const showAbnField = ABN_COUNTRIES.includes(selectedCountry);
    const abnRequired = showAbnField && companyName && companyName.trim() !== '';
    const skipMelissa = SKIP_MELISSA_COUNTRIES.includes(selectedCountry);

    // Handle address finder selection
    const handleAddressSelected = useCallback(
      (suggestion: AddressSuggestion) => {
        const { address } = suggestion;
        const country = normalizeCountryCode(address.country) || selectedCountry;

        setValue('streetAddress1', address.streetAddress1);
        setValue('city', address.city);
        setValue('countryArea', address.countryArea || '');
        setValue('country', country);

        // Format UK postcodes with proper spacing
        let postalCode = address.postalCode;
        if (country === 'GB' && UK_POSTCODE_REGEX.test(postalCode)) {
          postalCode = formatUKPostcode(postalCode);
        }
        setValue('postalCode', postalCode);

        // Clear melissa errors when new address selected
        setMelissaErrors([]);
      },
      [setValue, selectedCountry]
    );

    // Melissa validation
    const validateAddressUsingMelissa = useCallback(async (): Promise<boolean> => {
      if (!validateWithMelissa || skipMelissa) {
        return true;
      }

      setMelissaErrors([]);

      const values = getValues();
      const formData = {
        streetAddress1: values.streetAddress1,
        streetAddress2: values.streetAddress2 || undefined,
        city: values.city,
        postalCode: values.postalCode,
        countryArea: values.countryArea || undefined,
        country: values.country,
      };

      const result = await validateAddress(formData);

      if (!result.isValid) {
        // Set errors on form fields
        result.errors.forEach((err) => {
          if (err.field !== 'general') {
            setError(err.field as keyof ExtendedAddressFormData, { message: err.message });
          }
        });
        setMelissaErrors(result.errors);
        return false;
      }

      // Auto-correct form with validated data
      if (result.correctedAddress) {
        setValue('streetAddress1', result.correctedAddress.streetAddress1);
        setValue('city', result.correctedAddress.city);
        setValue('postalCode', result.correctedAddress.postalCode);
        if (result.correctedAddress.countryArea) {
          setValue('countryArea', result.correctedAddress.countryArea);
        }
      }

      return true;
    }, [validateWithMelissa, skipMelissa, getValues, validateAddress, setError, setValue]);

    // Expose validation method via ref
    useImperativeHandle(ref, () => ({
      validateAddressUsingMelissa,
    }));

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const values = getValues();
      const parsed = countryAwareSchema.safeParse(values);
      if (!parsed.success) {
        clearErrors();
        for (const issue of parsed.error.issues) {
          const field = issue.path[0];
          if (isExtendedAddressFormField(field)) {
            setError(field, { message: issue.message });
          }
        }
        return;
      }

      // Validate with Melissa before submitting
      if (validateWithMelissa && !skipMelissa) {
        const isValid = await validateAddressUsingMelissa();
        if (!isValid) {
          return;
        }
      }

      await onSubmit(parsed.data, { isDefaultShipping, isDefaultBilling });
    };

    // Get general melissa error for display
    const generalMelissaError = melissaErrors.find((e) => e.field === 'general')?.message;

    let submitButtonLabel = submitLabel;
    if (isValidating) {
      submitButtonLabel = t('validating');
    } else if (isSubmitting) {
      submitButtonLabel = t('saving');
    }

    return (
      <form
        className={styles.form}
        onSubmit={handleFormSubmit}
        noValidate
        data-testid="AddressForm"
      >
        {/* 1. Title / Gender */}
        <div className={styles.field}>
          <Select
            label={titleFieldLabel}
            {...register('title')}
            options={titleOptions}
            error={errors.title?.message}
            fullWidth
            required
            placeholder={titleFieldLabel}
            data-testid="AddressFormTitle"
          />
        </div>

        {/* 2. Name Row */}
        {selectedCountry === 'JP' ? (
          languageCode === 'ja' || languageCode === 'zh' ? (
            <>
              <div className={styles.row}>
                <div className={styles.field}>
                  <Input
                    label={tForms('lastNameKanji')}
                    {...register('lastNameKanji')}
                    error={errors.lastNameKanji?.message}
                    fullWidth
                    required
                    data-testid="AddressFormLastNameKanji"
                  />
                </div>
                <div className={styles.field}>
                  <Input
                    label={tForms('firstNameKanji')}
                    {...register('firstNameKanji')}
                    error={errors.firstNameKanji?.message}
                    fullWidth
                    required
                    data-testid="AddressFormFirstNameKanji"
                  />
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.field}>
                  <Input
                    label={tForms('lastNameForJP')}
                    {...register('lastName')}
                    error={errors.lastName?.message}
                    fullWidth
                    required
                    data-testid="AddressFormLastName"
                  />
                </div>
                <div className={styles.field}>
                  <Input
                    label={tForms('firstNameForJP')}
                    {...register('firstName')}
                    error={errors.firstName?.message}
                    fullWidth
                    required
                    data-testid="AddressFormFirstName"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles.row}>
                <div className={styles.field}>
                  <Input
                    label={tForms('firstNameForJP')}
                    {...register('firstName')}
                    error={errors.firstName?.message}
                    fullWidth
                    required
                    data-testid="AddressFormFirstName"
                  />
                </div>
                <div className={styles.field}>
                  <Input
                    label={tForms('lastNameForJP')}
                    {...register('lastName')}
                    error={errors.lastName?.message}
                    fullWidth
                    required
                    data-testid="AddressFormLastName"
                  />
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.field}>
                  <Input
                    label={tForms('firstNameKanji')}
                    {...register('firstNameKanji')}
                    error={errors.firstNameKanji?.message}
                    fullWidth
                    required
                    data-testid="AddressFormFirstNameKanji"
                  />
                </div>
                <div className={styles.field}>
                  <Input
                    label={tForms('lastNameKanji')}
                    {...register('lastNameKanji')}
                    error={errors.lastNameKanji?.message}
                    fullWidth
                    required
                    data-testid="AddressFormLastNameKanji"
                  />
                </div>
              </div>
            </>
          )
        ) : (
          <div className={styles.row}>
            <div className={styles.field}>
              <Input
                label={tForms('firstName')}
                {...register('firstName')}
                error={errors.firstName?.message}
                fullWidth
                required
                data-testid="AddressFormFirstName"
              />
            </div>
            <div className={styles.field}>
              <Input
                label={tForms('lastName')}
                {...register('lastName')}
                error={errors.lastName?.message}
                fullWidth
                required
                data-testid="AddressFormLastName"
              />
            </div>
          </div>
        )}

        {/* 3. Address Finder (Melissa autocomplete) — shown before manual fields */}
        {showAddressFinder && !skipMelissa && (
          <AddressFinder
            countryCode={melissaCountryCode}
            onAddressSelected={handleAddressSelected}
            disabled={isSubmitting || isValidating}
          />
        )}

        {/* 3. Company (optional) */}
        <div className={styles.field}>
          <Input
            label={t('companyOptional')}
            {...register('companyName')}
            error={errors.companyName?.message}
            fullWidth
            data-testid="AddressFormCompany"
          />
        </div>

        {/* 4. VAT Number (UK only) */}
        {showVatField && (
          <div className={styles.field}>
            <Input
              label={t('vatOptional')}
              {...register('vatNumber')}
              fullWidth
              data-testid="AddressFormVatNumber"
            />
          </div>
        )}

        {/* 5. ABN (Australia only, required if company provided) */}
        {showAbnField && (
          <div className={styles.field}>
            <Input
              label={abnRequired ? t('abn') : t('abnOptional')}
              {...register('abn')}
              fullWidth
              required={!!abnRequired}
              data-testid="AddressFormAbn"
            />
          </div>
        )}

        {/* 6. Street name + APT/STE/FL side-by-side */}
        <div className={styles.row}>
          <div className={styles.field}>
            <Input
              label={t('streetName')}
              {...register('streetAddress1')}
              error={errors.streetAddress1?.message}
              fullWidth
              required
              data-testid="AddressFormStreet1"
            />
          </div>
          <div className={styles.field}>
            <Input
              label={t('aptOptional')}
              {...register('streetAddress2')}
              error={errors.streetAddress2?.message}
              fullWidth
              data-testid="AddressFormStreet2"
            />
          </div>
        </div>

        {/* 7. Country area + City */}
        <div className={styles.row}>
          <div className={styles.field}>
            {countryAreaConfig.hasChoices ? (
              <Controller
                name="countryArea"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    label={countryAreaConfig.label}
                    options={countryAreaConfig.options}
                    error={errors.countryArea?.message}
                    fullWidth
                    required={countryAreaConfig.isRequired}
                    data-testid="AddressFormCountryArea"
                  />
                )}
              />
            ) : (
              <Input
                label={countryAreaConfig.label}
                {...register('countryArea')}
                error={errors.countryArea?.message}
                fullWidth
                required={countryAreaConfig.isRequired}
                data-testid="AddressFormCountryArea"
              />
            )}
          </div>
          <div className={styles.field}>
            <Input
              label={t('city')}
              {...register('city')}
              error={errors.city?.message}
              fullWidth
              required
              data-testid="AddressFormCity"
            />
          </div>
        </div>

        {/* 8. Postal Code + Country side-by-side */}
        <div className={styles.row}>
          <div className={styles.field}>
            <Input
              label={t('postalCode')}
              {...register('postalCode')}
              error={errors.postalCode?.message}
              fullWidth
              required
              data-testid="AddressFormPostalCode"
            />
          </div>
          <div className={styles.field}>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <Select
                  label={t('country')}
                  {...field}
                  options={sortedCountries}
                  error={errors.country?.message}
                  fullWidth
                  required
                  onChange={(event) => {
                    const nextCountry = event.target.value;
                    if (!nextCountry) {
                      field.onChange(field.value || initialCountry);
                      return;
                    }
                    field.onChange(nextCountry);
                    setValue('countryArea', '');
                  }}
                  data-testid="AddressFormCountry"
                />
              )}
            />
          </div>
        </div>

        {/* General Melissa Error */}
        {generalMelissaError && (
          <div className={styles.validationError} role="alert">
            {generalMelissaError}
          </div>
        )}

        {/* Default Address Checkboxes (for create mode) */}
        {showDefaultCheckboxes && (
          <div className={styles.checkboxes}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={isDefaultShipping}
                onChange={(e) => setIsDefaultShipping(e.target.checked)}
                data-testid="AddressFormDefaultShipping"
              />
              <span>{t('shippingAddress')}</span>
            </label>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={isDefaultBilling}
                onChange={(e) => setIsDefaultBilling(e.target.checked)}
                data-testid="AddressFormDefaultBilling"
              />
              <span>{t('billingAddress')}</span>
            </label>
          </div>
        )}

        {/* Submit / Cancel Buttons */}
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="secondary"
            disabled={isSubmitting || isValidating || countryAreaConfig.isLoading}
            loading={isSubmitting || isValidating || countryAreaConfig.isLoading}
            iconAfter={<Icon name="chevron-right" size={16} aria-hidden="true" />}
            data-testid="AddressFormSubmit"
          >
            {submitButtonLabel}
          </Button>
          {cancelHref && (
            <LinkButton
              href={cancelHref}
              variant="outline"
              iconAfter={<Icon name="chevron-right" size={16} aria-hidden="true" />}
              data-testid="AddressFormCancel"
            >
              {t('cancel')}
            </LinkButton>
          )}
        </div>
      </form>
    );
  }
);

AddressForm.displayName = 'AddressForm';
